import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/src/documentNode';
import * as Apollo from '@apollo/client';
export const WorkspaceBoardsViewDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"WorkspaceBoardsView"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"id"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"organization"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"id"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"limits"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"orgs"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"freeBoardsPerOrg"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"count"}},{"kind":"Field","name":{"kind":"Name","value":"disableAt"}},{"kind":"Field","name":{"kind":"Name","value":"status"}},{"kind":"Field","name":{"kind":"Name","value":"warnAt"}}]}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"offering"}},{"kind":"Field","name":{"kind":"Name","value":"premiumFeatures"}},{"kind":"Field","name":{"kind":"Name","value":"tags"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"name"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"WorkspaceBoardsView","document":WorkspaceBoardsViewDocument}} as const;
export type WorkspaceBoardsViewQueryVariables = Types.Exact<{
  id: Types.Scalars['ID']['input'];
}>;


export type WorkspaceBoardsViewQuery = (
  { __typename: 'Query' }
  & { organization?: Types.Maybe<(
    { __typename: 'Organization' }
    & Pick<Types.Organization, 'id' | 'name' | 'offering' | 'premiumFeatures'>
    & { limits: (
      { __typename: 'Organization_Limits' }
      & { orgs: (
        { __typename: 'Organization_Limits_Orgs' }
        & { freeBoardsPerOrg: (
          { __typename: 'Limit' }
          & Pick<Types.Limit, 'count' | 'disableAt' | 'status' | 'warnAt'>
        ) }
      ) }
    ), tags: Array<(
      { __typename: 'Tag' }
      & Pick<Types.Tag, 'id' | 'name'>
    )> }
  )> }
);

/**
 * __useWorkspaceBoardsViewQuery__
 *
 * To run a query within a React component, call `useWorkspaceBoardsViewQuery` and pass it any options that fit your needs.
 * When your component renders, `useWorkspaceBoardsViewQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useWorkspaceBoardsViewQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useWorkspaceBoardsViewQuery(
  baseOptions: TrelloQueryHookOptions<
    WorkspaceBoardsViewQuery,
    WorkspaceBoardsViewQueryVariables
  > &
    (
      | { variables: WorkspaceBoardsViewQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: WorkspaceBoardsViewDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    WorkspaceBoardsViewQuery,
    WorkspaceBoardsViewQueryVariables
  >(WorkspaceBoardsViewDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useWorkspaceBoardsViewLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    WorkspaceBoardsViewQuery,
    WorkspaceBoardsViewQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    WorkspaceBoardsViewQuery,
    WorkspaceBoardsViewQueryVariables
  >(WorkspaceBoardsViewDocument, options);
}
export function useWorkspaceBoardsViewSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        WorkspaceBoardsViewQuery,
        WorkspaceBoardsViewQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    WorkspaceBoardsViewQuery,
    WorkspaceBoardsViewQueryVariables
  >(WorkspaceBoardsViewDocument, options);
}
export type WorkspaceBoardsViewQueryHookResult = ReturnType<
  typeof useWorkspaceBoardsViewQuery
>;
export type WorkspaceBoardsViewLazyQueryHookResult = ReturnType<
  typeof useWorkspaceBoardsViewLazyQuery
>;
export type WorkspaceBoardsViewSuspenseQueryHookResult = ReturnType<
  typeof useWorkspaceBoardsViewSuspenseQuery
>;
export type WorkspaceBoardsViewQueryResult = Apollo.QueryResult<
  WorkspaceBoardsViewQuery,
  WorkspaceBoardsViewQueryVariables
>;
